import { cloneElement } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LoadScript } from '@react-google-maps/api';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import {
  ProtectedRoute, RoleProtectedRoute, SubscriptionProtectedRoute, PublicOnlyRoute,
} from '@/components/route-guards';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal';
import ImpersonationBanner from '@/components/ImpersonationBanner';

// Legacy pages still use the global axios instance. Inject the auth header
// + 401 fallback so they keep working alongside the new lib/api.js client.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    if (status === 402 && detail?.error === 'insufficient_credits') {
      window.dispatchEvent(new CustomEvent('photoplan:insufficient-credits', { detail }));
    }
    if (status === 401) {
      window.dispatchEvent(new CustomEvent('photoplan:logout'));
    }
    return Promise.reject(error);
  }
);

// Bridge: existing pages still expect a `user` prop. AuthProvider holds the source of truth.
function WithUser({ children }) {
  const { user } = useAuth();
  return cloneElement(children, { user });
}

import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Pricing from '@/pages/Pricing';
import TrialEnded from '@/pages/TrialEnded';
import Billing from '@/pages/Billing';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';

import Dashboard from '@/pages/Dashboard';
import Events from '@/pages/Events';
import EventDetail from '@/pages/EventDetail';
import PlanBuilder from '@/pages/PlanBuilderEnhanced';
import PlanBoard from '@/pages/PlanBoard';
import PhotographerView from '@/pages/PhotographerView';
import Photographers from '@/pages/Photographers';
import ImageLibrary from '@/pages/ImageLibrary';
import ContentTemplates from '@/pages/ContentTemplates';
import AssignmentManager from '@/pages/AssignmentManager';
import Gear from '@/pages/Gear';
import AdminPanel from '@/pages/AdminPanel';
import SharedAssignment from '@/pages/SharedAssignment';
import PlanSharePublic from '@/pages/PlanSharePublic';
import PhotographerLogin from '@/pages/PhotographerLogin';
import PhotographerVerify from '@/pages/PhotographerVerify';
import PhotographerDashboard from '@/pages/PhotographerDashboard';
import PhotographerEventView from '@/pages/PhotographerEventView';
import BackendLogin from '@/pages/BackendLogin';
import Profile from '@/pages/Profile';
import Team from '@/pages/Team';
import TeamAccept from '@/pages/TeamAccept';
import LiveMasterControl from '@/pages/LiveMasterControl';
import Photos from '@/pages/Photos';
import Clients from '@/pages/Clients';
import Quotes from '@/pages/Quotes';
import Proposals from '@/pages/Proposals';
import Invoices from '@/pages/Invoices';
import Timesheets from '@/pages/Timesheets';
import Reports from '@/pages/Reports';
import PublicQuote from '@/pages/PublicQuote';
import PublicProposal from '@/pages/PublicProposal';
import PublicInvoice from '@/pages/PublicInvoice';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/share/:token" element={<SharedAssignment />} />
      <Route path="/p/:token" element={<PlanSharePublic />} />
      <Route path="/share/quote/:token" element={<PublicQuote />} />
      <Route path="/share/proposal/:token" element={<PublicProposal />} />
      <Route path="/share/invoice/:token" element={<PublicInvoice />} />

      {/* Photographer (separate auth via photographer_token) */}
      <Route path="/photographer/login" element={<PhotographerLogin />} />
      <Route path="/photographer/verify" element={<PhotographerVerify />} />
      <Route path="/photographer/dashboard" element={<PhotographerDashboard />} />
      <Route path="/photographer/event/:eventId" element={<PhotographerEventView />} />
      <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
      <Route path="/backend-login" element={<BackendLogin />} />

      {/* Trial-ended: protected (need auth) but allow frozen */}
      <Route path="/trial-ended" element={
        <SubscriptionProtectedRoute allowFrozen><TrialEnded /></SubscriptionProtectedRoute>
      } />

      {/* Billing: protected, allow frozen so users can manage payment */}
      <Route path="/billing" element={
        <SubscriptionProtectedRoute allowFrozen><Billing /></SubscriptionProtectedRoute>
      } />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />

      {/* App routes — require active subscription */}
      <Route path="/dashboard" element={<SubscriptionProtectedRoute><WithUser><Dashboard /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events" element={<SubscriptionProtectedRoute><WithUser><Events /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events/:eventId" element={<SubscriptionProtectedRoute><WithUser><EventDetail /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events/:eventId/assignments" element={<SubscriptionProtectedRoute><WithUser><AssignmentManager /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events/:eventId/plan/:planId?" element={<SubscriptionProtectedRoute><WithUser><PlanBuilder /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events/:eventId/plan/:planId/live" element={<SubscriptionProtectedRoute><WithUser><LiveMasterControl /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/events/:eventId/plans/:planId" element={<SubscriptionProtectedRoute><WithUser><PlanBoard /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/photographers" element={<SubscriptionProtectedRoute><WithUser><Photographers /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/image-library" element={<SubscriptionProtectedRoute><WithUser><ImageLibrary /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/content-templates" element={<SubscriptionProtectedRoute><WithUser><ContentTemplates /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/gear" element={<SubscriptionProtectedRoute><WithUser><Gear /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/photos" element={<SubscriptionProtectedRoute><WithUser><Photos /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/clients" element={<SubscriptionProtectedRoute><WithUser><Clients /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/quotes" element={<SubscriptionProtectedRoute><WithUser><Quotes /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/quotes/:quoteId" element={<SubscriptionProtectedRoute><WithUser><Quotes /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/proposals" element={<SubscriptionProtectedRoute><WithUser><Proposals /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/proposals/:proposalId" element={<SubscriptionProtectedRoute><WithUser><Proposals /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/invoices" element={<SubscriptionProtectedRoute><WithUser><Invoices /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/invoices/:invoiceId" element={<SubscriptionProtectedRoute><WithUser><Invoices /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/timesheets" element={<SubscriptionProtectedRoute><WithUser><Timesheets /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/reports" element={<SubscriptionProtectedRoute><WithUser><Reports /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/photographer/roster" element={<ProtectedRoute><WithUser><PhotographerView /></WithUser></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><WithUser><Profile /></WithUser></ProtectedRoute>} />
      <Route path="/team" element={<SubscriptionProtectedRoute><WithUser><Team /></WithUser></SubscriptionProtectedRoute>} />
      <Route path="/team/accept" element={<TeamAccept />} />

      {/* Admin (gated by role; entry point is /backend-login) */}
      <Route path="/admin" element={
        <RoleProtectedRoute roles={['admin']}><AdminPanel /></RoleProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY} libraries={['places']}>
      <BrowserRouter>
        <AuthProvider>
          <div className="App">
            <ImpersonationBanner />
            <AppRoutes />
            <InsufficientCreditsModal />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </LoadScript>
  );
}
